// Időkapu-foglaló · Part 1 — Factory setup screen (Gyári tenant admin).
//
// 4 accordion sections:
//   1. Dokkok            — CRUD (név, irány, aktív)
//   2. Behajtási kapuk   — CRUD (kód, leírás, aktív)
//   3. Fogadási idősávok & idő paraméterek
//   4. Foglalási mezők   — kötelező / módosítható per mező
//
// Access is gated at App.js by RouteGuard + the module needs
// `user.dock_scheduling_enabled === true` AND `dock_roles.includes('dock_admin')`.
// (The FE menu simply hides the link when the gate fails.)

import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Plus, Trash2, Save, ChevronDown, ChevronRight, DoorOpen, Fence, Sliders, ListChecks } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dock-scheduling`;
const BLUE = "#185FA5";

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: size,
  fontWeight: weight || 700,
  letterSpacing: ls,
});

const DAY_NAMES_HU = ["Hétfő", "Kedd", "Szerda", "Csütörtök", "Péntek", "Szombat", "Vasárnap"];
const DIRECTION_LABELS = { inbound: "Bejövő", outbound: "Kimenő", both: "Mindkét irány" };
const FIELD_TYPE_LABELS = { text: "Szöveg", number: "Szám", plate: "Rendszám", date: "Dátum" };

// ─── Data hooks ─────────────────────────────────────────────────────────────

const useDocks = () => useQuery({
  queryKey: ["ds-docks"],
  queryFn: () => axios.get(`${API}/docks`).then(r => r.data.items || []),
});
const useGates = () => useQuery({
  queryKey: ["ds-gates"],
  queryFn: () => axios.get(`${API}/gates`).then(r => r.data.items || []),
});
const useSlotConfig = () => useQuery({
  queryKey: ["ds-slot-config"],
  queryFn: () => axios.get(`${API}/slot-config`).then(r => r.data),
});
const useFieldDefs = () => useQuery({
  queryKey: ["ds-field-defs"],
  queryFn: () => axios.get(`${API}/field-defs`).then(r => r.data.items || []),
});

// ─── Section wrapper ────────────────────────────────────────────────────────

function Section({ id, icon: Icon, title, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      data-testid={`ds-section-${id}`}
      className="mb-3 rounded-md overflow-hidden"
      style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`ds-section-toggle-${id}`}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        style={{ background: "transparent", border: 0, cursor: "pointer" }}
      >
        {open ? <ChevronDown size={16} color={BLUE} /> : <ChevronRight size={16} color={BLUE} />}
        <Icon size={18} color={BLUE} />
        <div>
          <div style={syne(15, 700)}>{title}</div>
          {subtitle && <div className="mt-0.5" style={mono(10, "var(--dimmer, #9198b0)")}>{subtitle}</div>}
        </div>
      </button>
      {open && <div className="px-4 pb-4 pt-2 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>{children}</div>}
    </section>
  );
}

// ─── Docks section ──────────────────────────────────────────────────────────

function DocksSection() {
  const qc = useQueryClient();
  const { data: docks = [], isLoading } = useDocks();
  const [draft, setDraft] = useState({ name: "", direction: "both", active: true });

  const create = useMutation({
    mutationFn: (body) => axios.post(`${API}/docks`, body).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ds-docks"] }); setDraft({ name: "", direction: "both", active: true }); toast.success("Dokk létrehozva"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba"),
  });
  const patch = useMutation({
    mutationFn: ({ id, body }) => axios.patch(`${API}/docks/${id}`, body).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ds-docks"] }); toast.success("Dokk frissítve"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba"),
  });
  const del = useMutation({
    mutationFn: (id) => axios.delete(`${API}/docks/${id}`).then(() => id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ds-docks"] }); toast.success("Dokk törölve"); },
  });

  return (
    <div data-testid="ds-docks-panel">
      <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
            <th className="text-left py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}>NÉV</th>
            <th className="text-left py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}>IRÁNY</th>
            <th className="text-left py-2" style={mono(10, "var(--dimmer, #9198b0)", 700, "0.4px")}>AKTÍV</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td colSpan={4} className="py-4" style={mono(11, "var(--dimmer)")}>Betöltés…</td></tr>}
          {!isLoading && docks.length === 0 && <tr><td colSpan={4} className="py-4" style={mono(11, "var(--dimmer)")}>Nincs dokk rögzítve.</td></tr>}
          {docks.map(d => (
            <tr key={d.id} data-testid={`ds-dock-row-${d.id}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <td className="py-2">
                <input
                  defaultValue={d.name}
                  onBlur={(e) => e.target.value !== d.name && patch.mutate({ id: d.id, body: { name: e.target.value } })}
                  data-testid={`ds-dock-name-${d.id}`}
                  className="px-2 py-1 rounded"
                  style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
                />
              </td>
              <td className="py-2">
                <select
                  defaultValue={d.direction}
                  onChange={(e) => patch.mutate({ id: d.id, body: { direction: e.target.value } })}
                  data-testid={`ds-dock-direction-${d.id}`}
                  className="px-2 py-1 rounded"
                  style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
                >
                  {Object.entries(DIRECTION_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </td>
              <td className="py-2">
                <input
                  type="checkbox"
                  defaultChecked={d.active}
                  onChange={(e) => patch.mutate({ id: d.id, body: { active: e.target.checked } })}
                  data-testid={`ds-dock-active-${d.id}`}
                />
              </td>
              <td className="py-2 text-right">
                <button
                  type="button"
                  onClick={() => window.confirm(`Törli a "${d.name}" dokkot?`) && del.mutate(d.id)}
                  data-testid={`ds-dock-delete-${d.id}`}
                  className="p-1 rounded"
                  style={{ background: "transparent", border: 0, color: "#f87171", cursor: "pointer" }}
                  title="Törlés"
                ><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex items-end gap-2 pt-3" style={{ borderTop: "1px dashed rgba(255,255,255,0.08)" }}>
        <div>
          <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>ÚJ DOKK NEVE</div>
          <input
            value={draft.name}
            onChange={(e) => setDraft(s => ({ ...s, name: e.target.value }))}
            placeholder="pl. Dokk 1"
            data-testid="ds-dock-new-name"
            className="px-2 py-1 rounded"
            style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12), width: 200 }}
          />
        </div>
        <div>
          <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>IRÁNY</div>
          <select
            value={draft.direction}
            onChange={(e) => setDraft(s => ({ ...s, direction: e.target.value }))}
            data-testid="ds-dock-new-direction"
            className="px-2 py-1 rounded"
            style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
          >
            {Object.entries(DIRECTION_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <button
          type="button"
          onClick={() => draft.name.trim() && create.mutate(draft)}
          data-testid="ds-dock-add"
          disabled={!draft.name.trim() || create.isPending}
          className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
          style={{ background: BLUE, color: "#fff", border: 0, cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
        ><Plus size={14} /> Hozzáadás</button>
      </div>
    </div>
  );
}

// ─── Gates section ──────────────────────────────────────────────────────────

function GatesSection() {
  const qc = useQueryClient();
  const { data: gates = [], isLoading } = useGates();
  const [draft, setDraft] = useState({ code: "", description: "", active: true });

  const create = useMutation({
    mutationFn: (body) => axios.post(`${API}/gates`, body).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ds-gates"] }); setDraft({ code: "", description: "", active: true }); toast.success("Kapu létrehozva"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba"),
  });
  const patch = useMutation({
    mutationFn: ({ id, body }) => axios.patch(`${API}/gates/${id}`, body).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ds-gates"] }),
  });
  const del = useMutation({
    mutationFn: (id) => axios.delete(`${API}/gates/${id}`).then(() => id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ds-gates"] }),
  });

  return (
    <div data-testid="ds-gates-panel">
      <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
            <th className="text-left py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>KÓD</th>
            <th className="text-left py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>LEÍRÁS</th>
            <th className="text-left py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>AKTÍV</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td colSpan={4} className="py-4" style={mono(11, "var(--dimmer)")}>Betöltés…</td></tr>}
          {!isLoading && gates.length === 0 && <tr><td colSpan={4} className="py-4" style={mono(11, "var(--dimmer)")}>Nincs kapu rögzítve.</td></tr>}
          {gates.map(g => (
            <tr key={g.id} data-testid={`ds-gate-row-${g.id}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <td className="py-2">
                <input
                  defaultValue={g.code}
                  onBlur={(e) => e.target.value !== g.code && patch.mutate({ id: g.id, body: { code: e.target.value } })}
                  data-testid={`ds-gate-code-${g.id}`}
                  className="px-2 py-1 rounded"
                  style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
                />
              </td>
              <td className="py-2">
                <input
                  defaultValue={g.description || ""}
                  onBlur={(e) => (e.target.value || "") !== (g.description || "") && patch.mutate({ id: g.id, body: { description: e.target.value } })}
                  data-testid={`ds-gate-desc-${g.id}`}
                  className="px-2 py-1 rounded w-full"
                  style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
                />
              </td>
              <td className="py-2">
                <input
                  type="checkbox"
                  defaultChecked={g.active}
                  onChange={(e) => patch.mutate({ id: g.id, body: { active: e.target.checked } })}
                  data-testid={`ds-gate-active-${g.id}`}
                />
              </td>
              <td className="py-2 text-right">
                <button
                  type="button"
                  onClick={() => window.confirm(`Törli a "${g.code}" kaput?`) && del.mutate(g.id)}
                  data-testid={`ds-gate-delete-${g.id}`}
                  className="p-1 rounded"
                  style={{ background: "transparent", border: 0, color: "#f87171", cursor: "pointer" }}
                ><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex items-end gap-2 pt-3" style={{ borderTop: "1px dashed rgba(255,255,255,0.08)" }}>
        <div>
          <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>ÚJ KAPUKÓD</div>
          <input
            value={draft.code}
            onChange={(e) => setDraft(s => ({ ...s, code: e.target.value }))}
            placeholder="pl. K-01"
            data-testid="ds-gate-new-code"
            className="px-2 py-1 rounded"
            style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12), width: 140 }}
          />
        </div>
        <div className="flex-1">
          <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>LEÍRÁS</div>
          <input
            value={draft.description}
            onChange={(e) => setDraft(s => ({ ...s, description: e.target.value }))}
            placeholder="pl. Északi teherportál"
            data-testid="ds-gate-new-desc"
            className="px-2 py-1 rounded w-full"
            style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
          />
        </div>
        <button
          type="button"
          onClick={() => draft.code.trim() && create.mutate(draft)}
          data-testid="ds-gate-add"
          disabled={!draft.code.trim() || create.isPending}
          className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
          style={{ background: BLUE, color: "#fff", border: 0, cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
        ><Plus size={14} /> Hozzáadás</button>
      </div>
    </div>
  );
}

// ─── Slot config section ────────────────────────────────────────────────────

function SlotConfigSection({ fieldDefs }) {
  const qc = useQueryClient();
  const { data: cfg, isLoading } = useSlotConfig();
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (cfg && !draft) {
      setDraft({
        reception_windows: cfg.reception_windows || [],
        slot_length_minutes: cfg.slot_length_minutes ?? 30,
        lead_time_hours: cfg.lead_time_hours ?? 24,
        modifiable_by_forwarder: !!cfg.modifiable_by_forwarder,
        modifiable_fields: cfg.modifiable_fields || [],
        timezone: cfg.timezone || "Europe/Budapest",
      });
    }
  }, [cfg, draft]);

  const save = useMutation({
    mutationFn: (body) => axios.put(`${API}/slot-config`, body).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ds-slot-config"] }); toast.success("Beállítás mentve"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba"),
  });

  const [leadUnit, setLeadUnit] = useState("hours");

  if (isLoading || !draft) return <div style={mono(11, "var(--dimmer)")}>Betöltés…</div>;

  const addWindow = () => setDraft(d => ({ ...d, reception_windows: [...d.reception_windows, { day_of_week: 0, start_time: "08:00", end_time: "16:00" }] }));
  const patchWindow = (i, patch) => setDraft(d => ({ ...d, reception_windows: d.reception_windows.map((w, j) => j === i ? { ...w, ...patch } : w) }));
  const removeWindow = (i) => setDraft(d => ({ ...d, reception_windows: d.reception_windows.filter((_, j) => j !== i) }));

  const toggleModifiableField = (key) => setDraft(d => ({
    ...d,
    modifiable_fields: d.modifiable_fields.includes(key)
      ? d.modifiable_fields.filter(k => k !== key)
      : [...d.modifiable_fields, key],
  }));

  const leadDisplay = leadUnit === "days" ? Math.round((draft.lead_time_hours / 24) * 10) / 10 : draft.lead_time_hours;
  const onLeadChange = (v) => {
    const num = parseFloat(v) || 0;
    setDraft(d => ({ ...d, lead_time_hours: leadUnit === "days" ? Math.round(num * 24) : Math.round(num) }));
  };

  return (
    <div data-testid="ds-slot-config-panel" className="space-y-4">
      {/* Reception windows */}
      <div>
        <div className="mb-2" style={syne(13, 700)}>Fogadási idősávok</div>
        <div className="space-y-2">
          {draft.reception_windows.length === 0 && (
            <div style={mono(11, "var(--dimmer)")}>Nincs beállított idősáv.</div>
          )}
          {draft.reception_windows.map((w, i) => (
            <div key={i} data-testid={`ds-window-${i}`} className="flex items-center gap-2">
              <select
                value={w.day_of_week}
                onChange={(e) => patchWindow(i, { day_of_week: parseInt(e.target.value, 10) })}
                data-testid={`ds-window-day-${i}`}
                className="px-2 py-1 rounded"
                style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
              >
                {DAY_NAMES_HU.map((n, d) => <option key={d} value={d}>{n}</option>)}
              </select>
              <input
                type="time"
                value={w.start_time}
                onChange={(e) => patchWindow(i, { start_time: e.target.value })}
                data-testid={`ds-window-start-${i}`}
                className="px-2 py-1 rounded"
                style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
              />
              <span style={mono(11, "var(--dimmer)")}>–</span>
              <input
                type="time"
                value={w.end_time}
                onChange={(e) => patchWindow(i, { end_time: e.target.value })}
                data-testid={`ds-window-end-${i}`}
                className="px-2 py-1 rounded"
                style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
              />
              <button
                type="button"
                onClick={() => removeWindow(i)}
                data-testid={`ds-window-remove-${i}`}
                className="p-1 rounded"
                style={{ background: "transparent", border: 0, color: "#f87171", cursor: "pointer" }}
              ><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addWindow}
          data-testid="ds-window-add"
          className="mt-2 px-2.5 py-1 rounded inline-flex items-center gap-1"
          style={{ background: "transparent", border: `1px solid ${BLUE}`, color: BLUE, cursor: "pointer", ...mono(10, undefined, 700, "0.4px") }}
        ><Plus size={13} /> ÚJ IDŐSÁV</button>
      </div>

      {/* Slot length + lead time */}
      <div className="flex flex-wrap gap-6 pt-2" style={{ borderTop: "1px dashed rgba(255,255,255,0.08)" }}>
        <div>
          <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>SLOT HOSSZ (PERC)</div>
          <input
            type="number" min={5} max={480}
            value={draft.slot_length_minutes}
            onChange={(e) => setDraft(d => ({ ...d, slot_length_minutes: parseInt(e.target.value || "0", 10) }))}
            data-testid="ds-slot-length"
            className="px-2 py-1 rounded mt-1"
            style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12), width: 100 }}
          />
        </div>
        <div>
          <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>ELŐFOGLALÁSI IDŐ</div>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number" min={0}
              value={leadDisplay}
              onChange={(e) => onLeadChange(e.target.value)}
              data-testid="ds-lead-time"
              className="px-2 py-1 rounded"
              style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12), width: 100 }}
            />
            <select
              value={leadUnit}
              onChange={(e) => setLeadUnit(e.target.value)}
              data-testid="ds-lead-unit"
              className="px-2 py-1 rounded"
              style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
            >
              <option value="hours">óra</option>
              <option value="days">nap</option>
            </select>
          </div>
        </div>
        <div>
          <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>IDŐZÓNA</div>
          <input
            value={draft.timezone}
            onChange={(e) => setDraft(d => ({ ...d, timezone: e.target.value }))}
            data-testid="ds-timezone"
            className="px-2 py-1 rounded mt-1"
            style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12), width: 160 }}
          />
        </div>
      </div>

      {/* Modifiable master toggle + per-field checkboxes */}
      <div className="pt-2" style={{ borderTop: "1px dashed rgba(255,255,255,0.08)" }}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.modifiable_by_forwarder}
            onChange={(e) => setDraft(d => ({ ...d, modifiable_by_forwarder: e.target.checked }))}
            data-testid="ds-modifiable-master"
          />
          <span style={syne(13, 700)}>A fuvarozó utólag módosíthat foglalást</span>
        </label>
        {draft.modifiable_by_forwarder && (
          <div className="mt-2 ml-6" data-testid="ds-modifiable-fields-block">
            <div className="mb-1" style={mono(10, "var(--dimmer)", 700, "0.4px")}>MÓDOSÍTHATÓ MEZŐK</div>
            <div className="grid grid-cols-2 gap-1.5">
              {fieldDefs.map(f => (
                <label key={f.field_key} className="flex items-center gap-2 cursor-pointer" data-testid={`ds-modifiable-field-${f.field_key}`}>
                  <input
                    type="checkbox"
                    checked={draft.modifiable_fields.includes(f.field_key)}
                    onChange={() => toggleModifiableField(f.field_key)}
                  />
                  <span style={mono(12)}>{f.label_hu}</span>
                </label>
              ))}
              {fieldDefs.length === 0 && <div style={mono(11, "var(--dimmer)")}>Nincs foglalási mező.</div>}
            </div>
          </div>
        )}
      </div>

      <div className="pt-3" style={{ borderTop: "1px dashed rgba(255,255,255,0.08)" }}>
        <button
          type="button"
          onClick={() => save.mutate(draft)}
          data-testid="ds-slot-config-save"
          disabled={save.isPending}
          className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
          style={{ background: BLUE, color: "#fff", border: 0, cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
        ><Save size={14} /> Mentés</button>
      </div>
    </div>
  );
}

// ─── Field defs section ─────────────────────────────────────────────────────

function FieldDefsSection({ fieldDefs, refetchAll }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (fieldDefs && !rows) setRows(fieldDefs.map(f => ({ ...f })));
  }, [fieldDefs, rows]);

  const save = useMutation({
    mutationFn: (body) => axios.put(`${API}/field-defs`, body).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ds-field-defs"] }); setRows(null); toast.success("Mezőlista mentve"); refetchAll && refetchAll(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba"),
  });

  if (!rows) return <div style={mono(11, "var(--dimmer)")}>Betöltés…</div>;

  const patchRow = (idx, patch) => setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const addRow = () => setRows(rs => [...rs, { field_key: "", label_hu: "", type: "text", is_required: false, is_modifiable: true, is_standard: false }]);
  const removeRow = (idx) => setRows(rs => rs.filter((_, i) => i !== idx));

  const canSave = rows.every(r => r.field_key.trim() && r.label_hu.trim());

  return (
    <div data-testid="ds-field-defs-panel">
      <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
            <th className="text-left py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>KULCS</th>
            <th className="text-left py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>CÍMKE (HU)</th>
            <th className="text-left py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>TÍPUS</th>
            <th className="text-center py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>KÖTELEZŐ</th>
            <th className="text-center py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>MÓDOSÍTHATÓ</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.field_key}-${i}`} data-testid={`ds-fielddef-row-${i}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <td className="py-2">
                <input
                  value={r.field_key}
                  onChange={(e) => patchRow(i, { field_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })}
                  disabled={r.is_standard}
                  data-testid={`ds-fielddef-key-${i}`}
                  className="px-2 py-1 rounded"
                  style={{ background: r.is_standard ? "rgba(255,255,255,0.03)" : "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(11), width: 140 }}
                />
              </td>
              <td className="py-2">
                <input
                  value={r.label_hu}
                  onChange={(e) => patchRow(i, { label_hu: e.target.value })}
                  data-testid={`ds-fielddef-label-${i}`}
                  className="px-2 py-1 rounded"
                  style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12), width: 200 }}
                />
              </td>
              <td className="py-2">
                <select
                  value={r.type}
                  onChange={(e) => patchRow(i, { type: e.target.value })}
                  data-testid={`ds-fielddef-type-${i}`}
                  className="px-2 py-1 rounded"
                  style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text)", ...mono(12) }}
                >
                  {Object.entries(FIELD_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </td>
              <td className="py-2 text-center">
                <input
                  type="checkbox"
                  checked={r.is_required}
                  onChange={(e) => patchRow(i, { is_required: e.target.checked })}
                  data-testid={`ds-fielddef-required-${i}`}
                />
              </td>
              <td className="py-2 text-center">
                <input
                  type="checkbox"
                  checked={r.is_modifiable}
                  onChange={(e) => patchRow(i, { is_modifiable: e.target.checked })}
                  data-testid={`ds-fielddef-modifiable-${i}`}
                />
              </td>
              <td className="py-2 text-right">
                {!r.is_standard && (
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    data-testid={`ds-fielddef-remove-${i}`}
                    className="p-1 rounded"
                    style={{ background: "transparent", border: 0, color: "#f87171", cursor: "pointer" }}
                  ><Trash2 size={14} /></button>
                )}
                {r.is_standard && (
                  <span data-testid={`ds-fielddef-standard-badge-${i}`} style={{ ...mono(9, "#9ec3ec", 700, "0.4px"), padding: "2px 6px", background: "rgba(24,95,165,0.15)", border: "1px solid rgba(24,95,165,0.4)", borderRadius: 4 }}>ALAP</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex items-center gap-2 pt-3" style={{ borderTop: "1px dashed rgba(255,255,255,0.08)" }}>
        <button
          type="button"
          onClick={addRow}
          data-testid="ds-fielddef-add"
          className="px-2.5 py-1 rounded inline-flex items-center gap-1"
          style={{ background: "transparent", border: `1px solid ${BLUE}`, color: BLUE, cursor: "pointer", ...mono(10, undefined, 700, "0.4px") }}
        ><Plus size={13} /> ÚJ MEZŐ</button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => save.mutate({ fields: rows.map(({ id, tenant_id, created_at, is_standard, ...rest }) => rest) })}
          data-testid="ds-fielddef-save"
          disabled={!canSave || save.isPending}
          className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
          style={{ background: canSave ? BLUE : "rgba(255,255,255,0.05)", color: canSave ? "#fff" : "var(--dimmer)", border: 0, cursor: canSave ? "pointer" : "not-allowed", ...mono(11, undefined, 700, "0.4px") }}
        ><Save size={14} /> Mentés</button>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function AdminDockScheduling() {
  const { data: fieldDefs = [] } = useFieldDefs();

  return (
    <div data-testid="admin-dock-scheduling">
      <header className="mb-5">
        <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 30px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
          <CalendarClock size={26} color={BLUE} style={{ display: "inline", verticalAlign: -4, marginRight: 10 }} />
          Időkapu-foglaló · Beállítások
        </h1>
        <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
          Dokkok · behajtási kapuk · fogadási idősávok · foglalási mezők
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to="/dock-scheduling/calendar"
            data-testid="ds-open-calendar-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded"
            style={{ background: BLUE, color: "#fff", textDecoration: "none", ...mono(11, "#fff", 700, "0.4px") }}
          >
            <CalendarClock size={14} /> NAPTÁR MEGNYITÁSA →
          </Link>
          <Link
            to="/dock-scheduling/my-bookings"
            data-testid="ds-open-my-bookings-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded"
            style={{ background: "transparent", color: BLUE, border: `1px solid ${BLUE}`, textDecoration: "none", ...mono(11, undefined, 700, "0.4px") }}
          >
            <ListChecks size={14} /> FOGLALÁSAIM →
          </Link>
        </div>
      </header>

      <Section id="docks" icon={DoorOpen} title="Dokkok" subtitle="Fogadó dokkok neve és iránya" defaultOpen>
        <DocksSection />
      </Section>

      <Section id="gates" icon={Fence} title="Behajtási kapuk" subtitle="Beléptető kapuk kódja és leírása">
        <GatesSection />
      </Section>

      <Section id="slot-config" icon={Sliders} title="Fogadási idősávok & időzítés" subtitle="Napi idősávok · slot hossz · előfoglalási idő · módosíthatóság">
        <SlotConfigSection fieldDefs={fieldDefs} />
      </Section>

      <Section id="field-defs" icon={ListChecks} title="Foglalási mezők" subtitle="Rendszám · TO szám · szolgáltató · vonal · admin mezők">
        <FieldDefsSection fieldDefs={fieldDefs} />
      </Section>
    </div>
  );
}
